import { PageTarget } from "PFTypes/saved_filters";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";

import { useSavedViewsContext } from "../../../components/filters/saved_views_context/saved_views_context";
import { WorkOpportunitiesTab } from "../../constants/work_opportunities_tab";
import { useMarketplaceFilters } from "../../hooks/use_marketplace_filters";
import { useUnreadFilter } from "../../hooks/use_unread_filter";
import { useWorkOpportunitiesTab } from "./use_work_opportunities_tab";

type UseEmptyStateReturn = {
  title: string;
  message?: string;
  buttonLabel: string;
  handleButtonClick: VoidFunction;
};

export const useEmptyState = (isFiltered: boolean, viewsKey: PageTarget): UseEmptyStateReturn => {
  const history = useHistory();
  const { t } = useTranslation("marketplace", { keyPrefix: "workOpportunities.emptyState" });
  const currentTab = useWorkOpportunitiesTab();
  const { setSelectedViewId } = useSavedViewsContext(viewsKey);
  const { clearAllFilters } = useMarketplaceFilters();
  const { isUnreadOnly } = useUnreadFilter();

  const title = useMemo(() => {
    if (isUnreadOnly) {
      return t("youHaveSeenItAll");
    }

    return {
      [WorkOpportunitiesTab.MatchingRoles]: t("noMatchingRoles"),
      [WorkOpportunitiesTab.Interested]: t("noInterestedRoles"),
      [WorkOpportunitiesTab.SavedRoles]: t("noSavedRoles"),
      [WorkOpportunitiesTab.SharedWithMe]: t("noSharedRoles")
    }[currentTab!];
  }, [t, currentTab, isUnreadOnly]);

  const message = useMemo(() => {
    if (isFiltered) {
      return t("tryRemovingFilters");
    }

    if (isUnreadOnly) {
      return t("noUnreadRoles");
    }

    return {
      [WorkOpportunitiesTab.MatchingRoles]: t("addSkillsToProfile"),
      [WorkOpportunitiesTab.Interested]: t("showInterestInRoles"),
      [WorkOpportunitiesTab.SavedRoles]: t("trySavingSomeRoles")
    }[currentTab!];
  }, [t, isFiltered, currentTab, isUnreadOnly]);

  const [buttonLabel, handleButtonClick] = useMemo(() => {
    if (isFiltered) {
      return [
        t("clearAllFilters"),
        () => {
          clearAllFilters();
          setSelectedViewId(null);
        }
      ];
    }

    if (currentTab === WorkOpportunitiesTab.MatchingRoles && !isUnreadOnly) {
      return [
        t("editProfile"),
        () => {
          history.push("/profiles/me");
        }
      ];
    }

    return [
      t("viewMatchingRoles"),
      () => {
        history.push("/marketplace/work_opportunities/matching_roles");
      }
    ];
  }, [isFiltered, currentTab, t, clearAllFilters, setSelectedViewId, history, isUnreadOnly]);

  return {
    title,
    message,
    buttonLabel,
    handleButtonClick
  };
};
